import { createContext, useContext, useState, useCallback, useRef } from "react";

const AIAssistantContext = createContext(null);

export const AIAssistantProvider = ({ children }) => {
  // Store page action handlers
  const pageHandlersRef = useRef({});
  
  // Store current page state that AI can read
  const [pageState, setPageState] = useState({});
  
  // Register handlers for a specific page
  const registerPageHandlers = useCallback((pageName, handlers) => {
    pageHandlersRef.current[pageName] = handlers;
    return () => {
      delete pageHandlersRef.current[pageName];
    };
  }, []);
  
  // Update page state that AI can access
  const updatePageState = useCallback((pageName, state) => {
    setPageState(prev => ({
      ...prev,
      [pageName]: state
    }));
  }, []);
  
  // Execute an action on the current page
  const executePageAction = useCallback((pageName, action, params) => {
    const handlers = pageHandlersRef.current[pageName];
    if (handlers && handlers[action]) {
      return handlers[action](params);
    }
    console.warn(`No handler found for action: ${action} on page: ${pageName}`);
    return null;
  }, []);
  
  // Get current page state
  const getPageState = useCallback((pageName) => {
    return pageState[pageName] || {};
  }, [pageState]);
  
  // Get available actions for a page
  const getAvailableActions = useCallback((pageName) => {
    const handlers = pageHandlersRef.current[pageName];
    return handlers ? Object.keys(handlers) : [];
  }, []);
  
  return (
    <AIAssistantContext.Provider value={{
      registerPageHandlers,
      updatePageState,
      executePageAction,
      getPageState,
      getAvailableActions,
      pageState
    }}>
      {children}
    </AIAssistantContext.Provider>
  );
};

export const useAIAssistant = () => {
  const context = useContext(AIAssistantContext);
  // Return safe defaults if context is not available
  if (!context) {
    return {
      registerPageHandlers: () => () => {},
      updatePageState: () => {},
      executePageAction: () => null,
      getPageState: () => ({}),
      getAvailableActions: () => [],
      pageState: {}
    };
  }
  return context;
};

export default AIAssistantContext;
